import { useState, useEffect } from 'react';
import { fetchTemplates, createTemplate, updateTemplate, deleteTemplate } from '../../api/relances.api';

const RELANCE_TYPES = {
  PAYMENT_REMINDER: { label: 'Rappel paiement', icon: '💰' },
  OVERDUE_INVOICE: { label: 'Facture impayée', icon: '📄' },
  PRESTATION_REMINDER: { label: 'Rappel prestation', icon: '🔧' },
  ORDER_FOLLOWUP: { label: 'Suivi commande', icon: '📦' },
  CUSTOM: { label: 'Personnalisé', icon: '✏️' }
};

const EMPTY_TEMPLATE = {
  code: '',
  name: '',
  relanceType: 'PAYMENT_REMINDER',
  emailSubject: '',
  emailBodyHtml: '',
  emailBodyText: '',
  whatsappMessage: '',
  isActive: true
};

const PREVIEW_VARS = {
  customer_name: 'Marie Martin',
  amount_due: '250,00 €',
  due_date: '15/03/2026',
  days_overdue: '12',
  reference: 'CMD-20260301-0042',
  invoice_number: 'CMD-20260301-0042',
  prestation_title: 'Installation matériel',
  order_number: 'CMD-20260228-0023',
  order_date: '28/02/2026',
  unsubscribe_link: '#'
};

function renderPreview(str) {
  if (!str) return '';
  return str.replace(/\{\{(\w+)\}\}/g, (_, key) => PREVIEW_VARS[key] || `{{${key}}}`);
}

export default function TemplateList({ onSelectTemplate }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({ ...EMPTY_TEMPLATE });
  const [saving, setSaving] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const data = await fetchTemplates();
      setTemplates(data);
    } catch (err) {
      console.error('Error loading templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingTemplate(null);
    setFormData({ ...EMPTY_TEMPLATE });
    setShowModal(true);
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      code: template.code,
      name: template.name,
      relanceType: template.relance_type,
      emailSubject: template.email_subject || '',
      emailBodyHtml: template.email_body_html || '',
      emailBodyText: template.email_body_text || '',
      whatsappMessage: template.whatsapp_message || '',
      isActive: template.is_active
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Supprimer ce template ?')) return;
    try {
      await deleteTemplate(id);
      loadTemplates();
    } catch (err) {
      alert('Erreur lors de la suppression');
    }
  };

  const handleSave = async () => {
    if (!formData.code || !formData.name) {
      alert('Code et nom requis');
      return;
    }

    setSaving(true);
    try {
      if (editingTemplate) {
        await updateTemplate(editingTemplate.id, formData);
      } else {
        await createTemplate(formData);
      }
      setShowModal(false);
      loadTemplates();
    } catch (err) {
      alert(err.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Templates de communication</h3>
          <p className="text-sm text-slate-500">
            Modèles réutilisables pour vos campagnes multi‑canal
          </p>
        </div>
        <button
          onClick={handleCreate}
          className="px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-950 flex items-center gap-2 text-sm font-semibold"
        >
          ➕ Nouveau template
        </button>
      </div>

      {/* Templates Grid */}
      {loading ? (
        <div className="text-center py-12 text-slate-500">Chargement...</div>
      ) : templates.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-slate-500 mb-4">Aucun template</p>
          <button onClick={handleCreate} className="text-slate-700 hover:underline">
            Créer votre premier template
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map(template => {
            const typeInfo = RELANCE_TYPES[template.relance_type] || RELANCE_TYPES.CUSTOM;

            return (
              <div
                key={template.id}
                className={`bg-white border rounded-xl p-4 ${
                  template.is_active
                    ? 'border-slate-200'
                    : 'border-slate-200 opacity-60'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span>{typeInfo.icon}</span>
                      <span className="font-medium text-slate-900">{template.name}</span>
                    </div>
                    <code className="text-xs bg-slate-100 px-1 py-0.5 rounded text-slate-600">
                      {template.code}
                    </code>
                  </div>
                  {!template.is_active && (
                    <span className="text-xs text-slate-500">Inactif</span>
                  )}
                </div>

                {/* Preview */}
                <div className="space-y-2 mb-4">
                  {template.email_subject && (
                    <div>
                      <div className="text-xs text-slate-500 mb-0.5">📧 Email</div>
                      <div className="text-sm text-slate-700 truncate">
                        {template.email_subject}
                      </div>
                    </div>
                  )}
                  {template.whatsapp_message && (
                    <div>
                      <div className="text-xs text-slate-500 mb-0.5">📱 WhatsApp</div>
                      <div className="text-sm text-slate-700 truncate">
                        {template.whatsapp_message}
                      </div>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex justify-between items-center pt-3 border-t border-slate-100">
                  <button
                    onClick={() => onSelectTemplate?.(template)}
                    className="text-sm text-slate-700 hover:text-slate-800 font-medium"
                  >
                    Utiliser
                  </button>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPreviewTemplate(template)}
                      className="text-sm text-sky-600 hover:text-sky-800"
                    >
                      Aperçu
                    </button>
                    <button
                      onClick={() => handleEdit(template)}
                      className="text-sm text-slate-500 hover:text-slate-700"
                    >
                      Modifier
                    </button>
                    {template.tenant_id && (
                      <button
                        onClick={() => handleDelete(template.id)}
                        className="text-sm text-red-500 hover:text-red-700"
                      >
                        Supprimer
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal Aperçu */}
      {previewTemplate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b border-slate-200 flex justify-between items-start">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Aperçu : {previewTemplate.name}</h3>
                <p className="text-xs text-slate-400 mt-0.5">Variables remplacées par des exemples</p>
              </div>
              <button onClick={() => setPreviewTemplate(null)} className="text-slate-400 hover:text-slate-700 text-xl">✕</button>
            </div>
            <div className="p-5 space-y-4">
              {previewTemplate.email_subject && (
                <div>
                  <div className="text-xs font-semibold text-slate-500 mb-1">📧 Sujet</div>
                  <div className="text-sm text-slate-900 bg-slate-50 rounded-lg px-3 py-2 border border-slate-200">
                    {renderPreview(previewTemplate.email_subject)}
                  </div>
                </div>
              )}
              {(previewTemplate.email_body_html || previewTemplate.email_body_text) && (
                <div>
                  <div className="text-xs font-semibold text-slate-500 mb-1">Corps de l'email</div>
                  <div
                    className="text-sm bg-white rounded-lg border border-slate-200 p-3 max-h-64 overflow-y-auto"
                    dangerouslySetInnerHTML={{ __html: renderPreview(previewTemplate.email_body_html || previewTemplate.email_body_text || '') }}
                  />
                </div>
              )}
              {previewTemplate.whatsapp_message && (
                <div>
                  <div className="text-xs font-semibold text-slate-500 mb-1">📱 WhatsApp</div>
                  <div className="text-sm text-slate-900 bg-green-50 rounded-lg px-3 py-2 border border-green-200 whitespace-pre-line">
                    {renderPreview(previewTemplate.whatsapp_message)}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900">
                {editingTemplate ? 'Modifier le template' : 'Nouveau template'}
              </h3>
            </div>

            <div className="p-6 space-y-4">
              {/* Code & Name */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Code *
                  </label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={e => setFormData(f => ({ ...f, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '') }))}
                    placeholder="PAYMENT_REMINDER_1"
                    disabled={!!editingTemplate}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-white disabled:opacity-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Nom *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={e => setFormData(f => ({ ...f, name: e.target.value }))}
                    placeholder="Rappel paiement - Standard"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-white"
                  />
                </div>
              </div>

              {/* Type & Active */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Type de relance
                  </label>
                  <select
                    value={formData.relanceType}
                    onChange={e => setFormData(f => ({ ...f, relanceType: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-white"
                  >
                    {Object.entries(RELANCE_TYPES).map(([key, val]) => (
                      <option key={key} value={key}>{val.icon} {val.label}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-end pb-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={e => setFormData(f => ({ ...f, isActive: e.target.checked }))}
                      className="rounded"
                    />
                    <span className="text-sm text-slate-700">Template actif</span>
                  </label>
                </div>
              </div>

              {/* Email Subject */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  📧 Sujet email
                </label>
                <input
                  type="text"
                  value={formData.emailSubject}
                  onChange={e => setFormData(f => ({ ...f, emailSubject: e.target.value }))}
                  placeholder="Rappel: Paiement en attente - {{amount_due}}"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-white"
                />
              </div>

              {/* Email Body */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Corps email (HTML ou texte)
                </label>
                <textarea
                  value={formData.emailBodyHtml || formData.emailBodyText}
                  onChange={e => setFormData(f => ({ ...f, emailBodyHtml: e.target.value, emailBodyText: e.target.value }))}
                  placeholder="Bonjour {{customer_name}},&#10;&#10;Nous vous rappelons qu'un paiement de {{amount_due}} est en attente depuis le {{due_date}}..."
                  rows={6}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-white font-mono text-sm"
                />
              </div>

              {/* WhatsApp */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  📱 Message WhatsApp
                </label>
                <textarea
                  value={formData.whatsappMessage}
                  onChange={e => setFormData(f => ({ ...f, whatsappMessage: e.target.value }))}
                  placeholder="Bonjour {{customer_name}}, rappel: paiement de {{amount_due}} en attente. Merci de régulariser."
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-white"
                />
              </div>

              {/* Variables Help */}
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="text-xs font-medium text-slate-500 mb-2">Variables disponibles</div>
                <div className="flex flex-wrap gap-2 text-xs">
                  {['customer_name', 'amount_due', 'due_date', 'days_overdue', 'reference'].map(v => (
                    <code key={v} className="bg-white px-2 py-1 rounded border border-slate-200">
                      {`{{${v}}}`}
                    </code>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-slate-600 hover:text-slate-800"
              >
                Annuler
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-950 disabled:opacity-50"
              >
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
